import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
interface LevelUpShockwaveProps {
  /** The new level that was just reached (used for label) */
  level: number;
  /** Called when the burst animation has fully finished */
  onComplete?: () => void;
  /** Extra className for the absolute-positioned container */
  className?: string;
}
/**
 * One-shot celebration that fires when the learner crosses a level boundary.
 *
 * Placed absolutely on top of the Sidebar / MobileNav XP-bar. Renders a radial
 * burst of rays + a glow ring + a "Level X!" badge. Auto-dismisses.
 *
 * Part of App-Feel 2027 §16.B — level-up shockwave. Mirrors the visual
 * vocabulary of {@link StreakMilestoneBurst} so the two celebrations feel like
 * siblings: same ray pattern, same glow-ring, same soft fade. Palette differs
 * (gold/indigo, matching the XP-bar gradient) so the learner can tell at a
 * glance which milestone just fired.
 *
 * The burst is stateless: the parent decides WHEN to render it by remounting
 * via a `key` prop. Respects `prefers-reduced-motion` via useMotionBudget —
 * falls back to a single static label so the level-up is still acknowledged.
 */
export function LevelUpShockwave({
  level,
  onComplete,
  className = ''
}: LevelUpShockwaveProps) {
  const {
    shouldAnimate
  } = useMotionBudget();
  const [isVisible, setIsVisible] = useState(true);
  useEffect(() => {
    const duration = shouldAnimate ? 1800 : 1100;
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, duration);
    return () => clearTimeout(timer);
  }, [shouldAnimate, onComplete]);
  // 10 rays in a full circle — same density as the 7-day streak milestone
  const rayCount = 10;
  const rays = Array.from({
    length: rayCount
  }, (_, i) => ({
    angle: i * 360 / rayCount,
    key: i
  }));
  return <AnimatePresence data-id="element-2322">
      {isVisible && <div className={`absolute inset-0 pointer-events-none z-30 flex items-center justify-center ${className}`} data-id="element-2323">
          {/* Radial rays — gold over the indigo/purple XP-bar */}
          {shouldAnimate && rays.map(({
        angle,
        key
      }) => <motion.div key={key} className="absolute top-1/2 left-1/2" style={{
        width: 2,
        height: 18,
        background: 'linear-gradient(to top, transparent, #fde047)',
        transformOrigin: 'bottom center',
        rotate: `${angle}deg`
      }} initial={{
        opacity: 0,
        scaleY: 0,
        x: -1,
        y: -18
      }} animate={{
        opacity: [0, 1, 0],
        scaleY: [0, 1, 1.4],
        y: [-18, -30, -38]
      }} transition={{
        duration: 0.8,
        ease: 'easeOut',
        delay: key % 4 * 0.03
      }} data-id="element-2324" />)}
          {/* Soft glow ring — matches XP-bar gradient endpoint (indigo) */}
          {shouldAnimate && <motion.div className="absolute rounded-full" style={{
        width: 48,
        height: 48,
        boxShadow: '0 0 14px 5px #6366f1',
        opacity: 0.5
      }} initial={{
        scale: 0.4,
        opacity: 0
      }} animate={{
        scale: [0.4, 1.3, 1.6],
        opacity: [0, 0.65, 0]
      }} transition={{
        duration: 1.0,
        ease: 'easeOut'
      }} data-id="element-2325" />}
          {/* Floating "Level X!" badge — anchored above the XP-bar */}
          <motion.div className="absolute -top-7 whitespace-nowrap" initial={{
        opacity: 0,
        y: 4,
        scale: 0.7
      }} animate={shouldAnimate ? {
        opacity: [0, 1, 1, 0],
        y: [4, -2, -2, -6],
        scale: [0.7, 1.05, 1, 0.95]
      } : {
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: shouldAnimate ? 1.7 : 0.2,
        times: shouldAnimate ? [0, 0.2, 0.8, 1] : undefined,
        ease: 'easeOut'
      }} data-id="element-2326">
            <div className="px-2 py-0.5 rounded-full text-[10px] font-black text-white shadow-md flex items-center gap-1" style={{
          background: 'linear-gradient(90deg, #6366f1, #8b5cf6)'
        }} data-id="element-2327">
              <span data-id="element-2328">⭐</span>
              <span data-id="element-2329">Level {level}!</span>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
/**
 * Duration of the choreographed level-up XP-bar sequence (ms).
 *
 * The parent uses this to time the XP-bar fill→burst→reset choreography:
 *  - Phase 1 (0 to FILL_MS): XP-bar animates to 100%
 *  - Phase 2 (FILL_MS to FILL_MS + BURST_MS): shockwave plays at 100%
 *  - Phase 3 (after): XP-bar snaps back to actual progress% for new level
 */
export const LEVEL_UP_FILL_MS = 450;
export const LEVEL_UP_BURST_MS = 700;